import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pressable, StyleSheet, View, ViewProps } from "react-native";
import { useHover } from "../hooks/useHover";
import { animDuration, colors, radii } from "../utils/design";
import { Icon } from "./Icon";
import { Space } from "./Space";
import { Text } from "./Text";

const CLOSE_DURATION = 200;
const AUTO_CLOSE_TIMEOUT = 5000;

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 16,
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: colors.neutral[800],
    borderRadius: radii[4],
    borderWidth: 1,
    borderColor: colors.neutral[700],
  },

  closeButton: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.negative[500],
    position: "absolute",
    left: -8,
    top: -8,
    transitionDuration: animDuration[200],
    transitionProperty: "transform opacity",
  },
  closeButtonPressed: {
    transform: "scale(0.9)",
  },
  closeButtonHidden: {
    transform: "scale(0.75)",
    opacity: 0,
  },

  iconContainer: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: colors.neutral[900],
    borderRadius: radii[4],
  },

  textContainer: {
    flex: 1,
  },

  toaster: {
    zIndex: 1000,
    position: "absolute",
    right: 16,
    bottom: 16,
    width: "calc(100% - 32px)",
    maxWidth: 400,
  },

  toasterToast: {
    position: "absolute",
    right: 0,
    bottom: 0,
    transformOrigin: "center bottom",
    transitionDuration: animDuration[CLOSE_DURATION],
    transitionProperty: "transform, opacity",

    animationKeyframes: {
      from: {
        transform: "translateY(100%)",
        opacity: 0,
      },
      to: {
        transform: "translateY(0)",
        opacity: 1,
      },
    },
    animationDuration: animDuration[200],
  },
});

type ToastParams = {
  variant: "success" | "error" | "info";
  title: string;
  message: React.ReactNode;
};

const toastColor: Record<ToastParams["variant"], string> = {
  success: colors.positive[500],
  error: colors.negative[500],
  info: colors.primary[500],
};

const toastIcon: Record<ToastParams["variant"], IconName> = {
  success: "check_circle",
  error: "warning_triangle",
  info: "info_circle",
};

type ToastProps = ToastParams & {
  viewRef?: React.MutableRefObject<View | null>;
  onClose?: () => void;
  style?: ViewProps["style"];
};

export const Toast: React.FC<ToastProps> = ({
  viewRef,
  variant,
  title,
  message,
  onClose,
  style,
}) => {
  const color = toastColor[variant];
  const closable = onClose != null;

  return (
    <View ref={viewRef} style={[styles.container, style]}>
      <Pressable
        style={({ pressed }) => [
          styles.closeButton,
          pressed && styles.closeButtonPressed,
          !closable && styles.closeButtonHidden,
        ]}
        disabled={!closable}
        aria-hidden={!closable}
        onPress={onClose}
      >
        <Icon name="close" color={colors.neutral[900]} size={16} />
      </Pressable>

      <View style={styles.iconContainer}>
        <Icon name={toastIcon[variant]} color={color} size={24} />
      </View>

      <Space width={12} />

      <View style={styles.textContainer}>
        <Text variant="small" color={color}>
          {title}
        </Text>
        <Text variant="small">{message}</Text>
      </View>
    </View>
  );
};

let pushToast: undefined | ((props: ToastParams) => void);
let lastToastId = 0;

// eslint-disable-next-line react-refresh/only-export-components
export const toast = (props: ToastParams) => {
  pushToast?.(props);
};

type ToasterState = ToastParams & { id: number };

export const Toaster: React.FC = () => {
  const [toasts, setToasts] = useState<ToasterState[]>([]);

  useEffect(() => {
    pushToast = (props) => {
      setToasts((prev) => [{ ...props, id: lastToastId++ }, ...prev]);
    };
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return createPortal(
    <View style={styles.toaster}>
      {toasts.map((toast, index) => (
        <ToasterToast
          key={toast.id}
          {...toast}
          position={index}
          nbToasts={toasts.length}
          onClose={removeToast}
        />
      ))}
    </View>,
    document.body,
  );
};

type ToasterToastProps = ToastParams & {
  id: number;
  position: number;
  nbToasts: number;
  onClose: (id: number) => void;
};

const NB_MAX_BACKGROUND_TOAST = 3;

const ToasterToast: React.FC<ToasterToastProps> = ({
  id,
  position,
  nbToasts,
  onClose,
  ...props
}) => {
  const ref = useRef<View | null>(null);
  const [closing, setClosing] = useState(false);
  const zIndex = nbToasts - position;
  const transformPositon = Math.min(position, NB_MAX_BACKGROUND_TOAST);
  const scale = 1 - transformPositon * 0.05;
  const transform = closing
    ? "translateY(100%)"
    : `translateY(-${12 * transformPositon}px) scale(${scale})`;
  const opacity = position > NB_MAX_BACKGROUND_TOAST || closing ? 0 : 1;

  const isLastOpened = position === 0;
  const withAutoClose = props.variant !== "error";

  const [hovered, setHovered] = useState(false);
  useHover(ref, {
    onHoverChange: setHovered,
  });

  const close = isLastOpened
    ? () => {
        setClosing(true);
        setTimeout(() => {
          onClose(id);
        }, CLOSE_DURATION);
      }
    : undefined;

  useEffect(() => {
    if (close != null && withAutoClose && !hovered) {
      const timeout = setTimeout(() => {
        setClosing(true);
        setTimeout(() => {
          onClose(id);
        }, CLOSE_DURATION);
      }, AUTO_CLOSE_TIMEOUT);

      return () => clearTimeout(timeout);
    }
    // we need to run this effect only when the position changes
    // or when hover state changes
  }, [close, hovered]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Toast
      {...props}
      viewRef={ref}
      style={[styles.toasterToast, { zIndex, transform, opacity }]}
      onClose={close}
    />
  );
};
